import * as fa from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FC, type MouseEvent, useState } from "react";

const maxWidthOptions = {
  md: "max-w-md",
  lg: "max-w-lg",
  full: "max-w-full",
};

interface MultiAutocompleteProps {
  label: string;
  options: { [key: string]: string };
  value: string[];
  onChange: (newValue: string[]) => void;
  maxWidth?: keyof typeof maxWidthOptions;
}

export const MultiAutocomplete: FC<MultiAutocompleteProps> = (props) => {
  const [search, setSearch] = useState("");
  const [hasFocus, setHasFocus] = useState(false);

  const valueSet = new Set(props.value);
  const selectedLabels = props.value.map((key) => props.options[key]).filter(Boolean);
  const selectedLabelsText =
    selectedLabels.length > 0 ? selectedLabels.join(", ") : "Niets geselecteerd";

  const allKeys = Object.keys(props.options);
  const allSelected = allKeys.length > 0 && allKeys.every((key) => valueSet.has(key));
  const options = Object.entries(props.options).filter(([, label]) =>
    label.toLowerCase().includes(search.toLowerCase()),
  );

  const toggleOption = (event: MouseEvent<HTMLButtonElement>, key: string) => {
    event.preventDefault();
    props.onChange(
      valueSet.has(key) ? props.value.filter((v) => v !== key) : [...props.value, key],
    );
  };
  const toggleAll = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    props.onChange(allSelected ? [] : allKeys);
  };

  return (
    <label
      className={`flex flex-col text-sm font-medium text-brand-900 w-full ${maxWidthOptions[props.maxWidth ?? "md"]}`}
    >
      {props.label}
      <div className={"relative mt-1"}>
        <input
          onFocus={() => {
            setHasFocus(true);
            setSearch("");
          }}
          onBlur={() => {
            setHasFocus(false);
            setSearch("");
          }}
          className={`border-2 border-slate-200 outline-brand-700 text-black rounded-sm text-base bg-white font-normal px-2 h-10 w-full ${!hasFocus && props.value.length === 0 ? "italic text-slate-400" : ""}`}
          onChange={(e) => setSearch(e.target.value)}
          value={hasFocus ? search : selectedLabelsText}
          placeholder={hasFocus ? selectedLabelsText : undefined}
        />

        {/* Chevron icon*/}
        <div className={"absolute top-0 right-2 h-full flex items-center justify-end"}>
          <FontAwesomeIcon icon={fa.faChevronDown} className={"text-xs text-brand-900"} />
        </div>

        {hasFocus && (
          <div
            className={
              "absolute top-10 left-0 w-full text-black bg-white rounded-sm shadow-sm z-10 max-h-55 overflow-y-scroll"
            }
          >
            <div className={"flex flex-col items-stretch"}>
              {allKeys.length > 0 && (
                <button
                  type={"button"}
                  className={
                    "h-10 w-full px-4 text-left font-medium hover:bg-slate-100 border-b border-slate-200"
                  }
                  onMouseDown={toggleAll}
                >
                  {allSelected ? "Deselecteer alles" : "Selecteer alles"} ({allKeys.length})
                </button>
              )}
              {options.map(([key, label]) => {
                const isSelected = valueSet.has(key);
                return (
                  <button
                    key={key}
                    type={"button"}
                    className={"h-10 w-full px-4 flex items-center hover:bg-slate-100 text-left"}
                    onMouseDown={(e) => toggleOption(e, key)}
                  >
                    <div
                      className={`h-4 w-4 flex-none flex items-center justify-center rounded-sm border-2 ${
                        isSelected
                          ? "border-brand-900 bg-brand-900 text-white"
                          : "border-slate-500 bg-transparent"
                      }`}
                    >
                      {isSelected && <FontAwesomeIcon icon={fa.faCheck} className={"h-3 w-3"} />}
                    </div>
                    <span className={`ml-2 ${isSelected ? "text-brand-800" : ""}`}>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </label>
  );
};
